
import {
  Contact,
  AddFromCrmResult,
  PromoteFromEventResult,
  PromotePreviewResult,
} from "@/types/Contact";
import {
  getContacts as getContactsApi,
  getContactsCount as getContactsCountApi,
  getContact as getContactApi,
  createContact as createContactApi,
  updateContact as updateContactApi,
  deleteContact as deleteContactApi,
  bulkDeleteContacts as bulkDeleteContactsApi,
  previewContactImportDuplicates as previewContactImportDuplicatesApi,
  addContactsToEvent as addContactsToEventApi,
  queryContacts as queryContactsApi,
  queryContactsCount as queryContactsCountApi,
  resolveContactIds as resolveContactIdsApi,
  matchContactsByEmails as matchContactsByEmailsApi,
  promoteContactsFromEvent as promoteContactsFromEventApi,
  previewPromoteFromEvent as previewPromoteFromEventApi,
  GetContactsParams,
  QueryContactsBody,
  QueryContactsFilterBody,
  ResolveContactIdsResult,
  ContactEmailMatch,
} from "@/lib/api/clients/ContactClient";
import { DedupePreviewResponse } from "@/lib/api/clients/GuestClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";

/**
 * Shared filter/paging validation for the list + count paths. Throws
 * "validationError" on malformed params so both handlers stay in sync.
 */
function validateContactListParams(params: GetContactsParams) {
  if (params.eventId !== undefined && !isValidObjectId(params.eventId))
    throw new Error("validationError");
  if (params.importId !== undefined && !isValidObjectId(params.importId))
    throw new Error("validationError");
  if (
    params.page !== undefined &&
    (!Number.isInteger(params.page) || params.page < 0)
  )
    throw new Error("validationError");
  if (
    params.size !== undefined &&
    (!Number.isInteger(params.size) || params.size <= 0 || params.size > 1000)
  )
    throw new Error("validationError");
  if (params.searchTerm !== undefined && params.searchTerm.length > 200)
    throw new Error("validationError");
  if (params.sortBy !== undefined) {
    // Same key pattern the export sort validation enforces — defense-in-depth
    // against `__proto__` / operator-injection keys.
    if (!/^(additionalFields\.)?[A-Za-z][A-Za-z0-9_]{0,99}$/.test(params.sortBy))
      throw new Error("validationError");
  }
  if (
    params.sortOrder !== undefined &&
    params.sortOrder !== "asc" &&
    params.sortOrder !== "desc"
  )
    throw new Error("validationError");
}

export async function getContacts(
  params: GetContactsParams = {},
): Promise<Contact[] | ServerError> {
  try {
    validateContactListParams(params);
    return await getContactsApi(params);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getContactsCount(
  params: Pick<GetContactsParams, "searchTerm" | "eventId" | "importId"> = {},
): Promise<number | ServerError> {
  try {
    validateContactListParams(params);
    return await getContactsCountApi(params);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getContact(
  contactId: string,
): Promise<Contact | ServerError> {
  try {
    if (!isValidObjectId(contactId)) throw new Error("validationError");
    return await getContactApi(contactId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function createContact(
  contact: Contact,
): Promise<string | ServerError> {
  try {
    if (!contact || typeof contact !== "object")
      throw new Error("validationError");
    // A contact must carry at least one identifying value — mirrors the
    // backend's minimal-create check; deep field validation stays server-side
    // (shared validators, no DataType switches here).
    if (
      !contact.firstName?.trim() &&
      !contact.lastName?.trim() &&
      !contact.email?.trim()
    )
      throw new Error("validationError");
    return await createContactApi(contact);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateContact(
  contactId: string,
  contact: Partial<Contact>,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(contactId)) throw new Error("validationError");
    if (!contact || typeof contact !== "object")
      throw new Error("validationError");
    return await updateContactApi(contactId, contact);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function deleteContact(
  contactId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(contactId)) throw new Error("validationError");
    return await deleteContactApi(contactId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function bulkDeleteContacts(
  contactIds: string[],
): Promise<void | ServerError> {
  try {
    if (!Array.isArray(contactIds)) throw new Error("validationError");
    if (contactIds.length === 0) throw new Error("validationError");
    // The backend's cap: `ContactService.deleteContacts` refuses more than
    // 1000 ids with `tooManyIds`. The 10000 that stood here mirrored nothing
    // (PB-UCRM-1).
    if (contactIds.length > 1000) throw new Error("validationError");
    for (const contactId of contactIds) {
      if (!isValidObjectId(contactId)) throw new Error("validationError");
    }
    return await bulkDeleteContactsApi([...new Set(contactIds)]);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function previewContactImportDuplicates(
  conditions: unknown,
  rows: Record<string, unknown>[],
): Promise<DedupePreviewResponse | ServerError> {
  try {
    if (!conditions) throw new Error("validationError");
    if (!Array.isArray(rows) || rows.length === 0)
      throw new Error("validationError");
    return await previewContactImportDuplicatesApi(conditions, rows);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function addContactsToEvent(
  eventId: string,
  contactIds: string[],
  skipAlreadyInEvent?: boolean,
): Promise<AddFromCrmResult | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!Array.isArray(contactIds)) throw new Error("validationError");
    if (contactIds.length === 0) throw new Error("validationError");
    if (contactIds.length > 10000) throw new Error("validationError");
    for (const contactId of contactIds) {
      if (!isValidObjectId(contactId)) throw new Error("validationError");
    }
    return await addContactsToEventApi(eventId, {
      contactIds: [...new Set(contactIds)],
      ...(skipAlreadyInEvent !== undefined && { skipAlreadyInEvent }),
    });
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Filter-body validation shared by the query/count/resolve POST paths.
 * Reuses the list validation for the overlapping params, then checks the
 * query-only additions (`notInEventId`, `conditions`). Paging is validated
 * by the caller where it applies (the query list path).
 */
function validateContactQueryFilter(body: QueryContactsFilterBody) {
  if (!body || typeof body !== "object") throw new Error("validationError");
  validateContactListParams({
    searchTerm: body.searchTerm,
    eventId: body.eventId,
    importId: body.importId,
  });
  if (body.notInEventId !== undefined && !isValidObjectId(body.notInEventId))
    throw new Error("validationError");
  if (body.conditions !== undefined) {
    if (
      !body.conditions ||
      typeof body.conditions !== "object" ||
      Array.isArray(body.conditions)
    )
      throw new Error("validationError");
    // Deep tree validation (key allowlist, depth, operators) is the backend
    // validator's job — here we only reject shapes that cannot be a group.
    if (body.conditions.operator !== "AND" && body.conditions.operator !== "OR")
      throw new Error("validationError");
  }
}

export async function queryContacts(
  body: QueryContactsBody,
): Promise<Contact[] | ServerError> {
  try {
    validateContactQueryFilter(body);
    validateContactListParams({
      page: body.page,
      sortBy: body.sortBy,
      sortOrder: body.sortOrder,
    });
    // The query endpoint caps pages at 200 (tighter than the GET list's 1000).
    if (
      body.size !== undefined &&
      (!Number.isInteger(body.size) || body.size <= 0 || body.size > 200)
    )
      throw new Error("validationError");
    return await queryContactsApi(body);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function queryContactsCount(
  body: QueryContactsFilterBody,
): Promise<number | ServerError> {
  try {
    validateContactQueryFilter(body);
    return await queryContactsCountApi(body);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function resolveContactIds(
  body: QueryContactsFilterBody,
): Promise<ResolveContactIdsResult | ServerError> {
  try {
    validateContactQueryFilter(body);
    return await resolveContactIdsApi(body);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function matchContactsByEmails(
  emails: string[],
): Promise<{ matches: ContactEmailMatch[] } | ServerError> {
  try {
    if (!Array.isArray(emails)) throw new Error("validationError");
    // Blank entries are ignored (guests without an email simply don't
    // match), and duplicates are collapsed before hitting the 200 cap.
    const cleaned = [
      ...new Set(
        emails
          .filter((e): e is string => typeof e === "string")
          .map((e) => e.trim())
          .filter((e) => e !== ""),
      ),
    ];
    if (cleaned.length === 0) return { matches: [] };
    if (cleaned.length > 200) throw new Error("validationError");
    for (const email of cleaned) {
      if (email.length > 320) throw new Error("validationError");
    }
    return await matchContactsByEmailsApi(cleaned);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function promoteContactsFromEvent(
  eventId: string,
  guestIds: string[],
  forceSplitKeys?: string[],
): Promise<PromoteFromEventResult | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!Array.isArray(guestIds)) throw new Error("validationError");
    if (guestIds.length === 0) throw new Error("validationError");
    // Pre-chunk guard: callers chunk at ≤1000/call (backend cap); 10000 here
    // only catches grossly wrong call sites, mirroring bulkDeleteContacts.
    if (guestIds.length > 10000) throw new Error("validationError");
    for (const guestId of guestIds) {
      if (!isValidObjectId(guestId)) throw new Error("validationError");
    }
    // forceSplitKeys are opaque tokens from the preview; pass through only
    // non-empty strings, deduped (the backend ignores stale/unknown ones).
    const splitKeys =
      Array.isArray(forceSplitKeys) && forceSplitKeys.length > 0
        ? [...new Set(forceSplitKeys.filter((k) => typeof k === "string" && k.length > 0))]
        : undefined;
    return await promoteContactsFromEventApi(eventId, {
      guestIds: [...new Set(guestIds)],
      ...(splitKeys && splitKeys.length > 0 ? { forceSplitKeys: splitKeys } : {}),
    });
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Read-only collapse preview for the transfer confirm modal ("100 guests →
 * 1 contact"). Best-effort: returns null on ANY invalid input or failure —
 * the preview must never block the transfer flow (fetchGuestDeleteImpact
 * parity), callers render nothing on null.
 */
export async function fetchPromotePreview(
  eventId: string,
  guestIds: string[],
): Promise<PromotePreviewResult | null> {
  try {
    if (!isValidObjectId(eventId)) return null;
    if (!Array.isArray(guestIds)) return null;
    const unique = [...new Set(guestIds)];
    if (unique.length === 0 || unique.length > 5000) return null;
    for (const guestId of unique) {
      if (!isValidObjectId(guestId)) return null;
    }
    return await previewPromoteFromEventApi(eventId, { guestIds: unique });
  } catch {
    return null;
  }
}
